/**
 * src/services/userManagementService.js
 *
 * Service for admin user management: list users, update role,
 * toggle OnSite+, activate/deactivate accounts.
 *
 * Anti-lockout guardrails:
 *   - updateUserRole: blocks demotion of the last active admin
 *   - setUserActive: blocks deactivation of the last active admin
 */

import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

/**
 * Count active admins in the current org.
 * Used for last-admin guardrail checks.
 */
const countActiveAdmins = async (orgId) => {
  const { count, error } = await supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.eq('organization_id', orgId)?.eq('role', 'admin')?.eq('is_active', true);

  if (error) throw error;
  return count ?? 0;
};

/**
 * List all users in the current org.
 * Returns: id, email, full_name, logo_url, role, onsite_plus_enabled,
 *          can_view_all_projects, is_active
 * Sorted by full_name ascending.
 */
export const listUsers = async () => {
  try {
    const orgId = await getCurrentOrgId();
    const { data, error } = await supabase?.from('user_profiles')?.select('id, email, full_name, logo_url, role, trade_id, onsite_plus_enabled, can_view_all_projects, is_active')?.eq('organization_id', orgId)?.order('full_name', { ascending: true });

    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (error) {
    console.error('userManagementService.listUsers error:', error);
    return { data: [], error };
  }
};

/**
 * List active users in the current org filtered by role.
 * Used for role-filtered dropdowns in the project team section.
 *
 * @param {string} role - one of 'pm', 'supervisor', 'assistant', 'accountant', 'admin'
 * @returns {{ data: Array<{id, email, full_name}>, error }}
 */
export const listUsersByRole = async (role) => {
  try {
    const orgId = await getCurrentOrgId();
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name')
      ?.eq('organization_id', orgId)
      ?.eq('role', role)
      ?.eq('is_active', true)
      ?.order('full_name', { ascending: true });

    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (error) {
    console.error('userManagementService.listUsersByRole error:', error);
    return { data: [], error };
  }
};

/**
 * Les personnes dont la presence sur un chantier se planifie a la journee :
 * les travailleurs ET les surintendants.
 *
 * Fonction distincte de listUsersByRole, qui ne prend qu'un role et ne ramene pas
 * trade_id — l'horaire a besoin des deux roles et du metier dans la meme liste.
 * Le tri final est fait par l'appelant, qui regroupe par role.
 *
 * @returns {{ data: Array<{id, email, full_name, role, trade_id}>, error }}
 */
export const listSchedulableUsers = async () => {
  try {
    const orgId = await getCurrentOrgId();
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name, role, trade_id')
      ?.eq('organization_id', orgId)
      ?.in('role', ['worker', 'supervisor'])
      ?.eq('is_active', true)
      ?.order('full_name', { ascending: true });

    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (error) {
    console.error('userManagementService.listSchedulableUsers error:', error);
    return { data: [], error };
  }
};

/**
 * Update a user's role.
 * Performs last-admin check before demoting an admin.
 *
 * @param {string} userId   - target user's id
 * @param {string} newRole  - new role value
 * @param {string} currentRole - current role of the target user
 */
export const updateUserRole = async (userId, newRole, currentRole) => {
  try {
    const orgId = await getCurrentOrgId();

    // Last-admin guardrail: if we're demoting an admin, ensure at least one remains
    if (currentRole === 'admin' && newRole !== 'admin') {
      const activeAdminCount = await countActiveAdmins(orgId);
      if (activeAdminCount <= 1) {
        return {
          data: null,
          error: null,
          blocked: true,
          reason: 'last_admin',
        };
      }
    }

    const { data, error } = await supabase?.from('user_profiles')?.update({ role: newRole })?.eq('id', userId)?.eq('organization_id', orgId)?.select('id, role')?.single();

    if (error) throw error;
    return { data, error: null, blocked: false };
  } catch (error) {
    console.error('userManagementService.updateUserRole error:', error);
    return { data: null, error, blocked: false };
  }
};

/**
 * Toggle OnSite+ access for a user.
 *
 * @param {string} userId  - target user's id
 * @param {boolean} enabled - new value
 */
export const setOnsitePlusEnabled = async (userId, enabled) => {
  try {
    const orgId = await getCurrentOrgId();

    const { data, error } = await supabase?.from('user_profiles')?.update({ onsite_plus_enabled: enabled })?.eq('id', userId)?.eq('organization_id', orgId)?.select('id, onsite_plus_enabled')?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('userManagementService.setOnsitePlusEnabled error:', error);
    return { data: null, error };
  }
};

/**
 * Toggle can_view_all_projects access for a user.
 * Grants or revokes cross-project read access (delegation system).
 *
 * @param {string} userId   - target user's id
 * @param {boolean} canView - new value
 */
export const setCanViewAllProjects = async (userId, canView) => {
  try {
    const orgId = await getCurrentOrgId();

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ can_view_all_projects: canView })
      ?.eq('id', userId)
      ?.eq('organization_id', orgId)
      ?.select('id, can_view_all_projects')
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('userManagementService.setCanViewAllProjects error:', error);
    return { data: null, error };
  }
};

/**
 * Assign a permission template to a user.
 *
 * Le ROLE et le GABARIT sont deux choses differentes et le restent :
 *   role     = la fonction sur les projets (pm_user_id, supervisor_user_id...)
 *   gabarit  = les pages que la personne peut ouvrir
 * Un coordonnateur peut donc porter l'acces d'un charge de projet.
 *
 * GARDE ANTI-ENFERMEMENT : donner au dernier administrateur actif un gabarit
 * qui ne contient pas app.users lui fermerait la gestion des utilisateurs,
 * sans retour possible. Meme esprit que les gardes "dernier admin" du role et
 * de l'activation, mais la base ne peut pas voir ce lien-la.
 *
 * @param {string} userId      - target user's id
 * @param {string|null} templateId - template id, or null to clear
 * @param {object} target      - { role, is_active } of the target user
 * @param {string[]} templatePages - pages of the template being assigned
 */
export const setPermissionTemplate = async (userId, templateId, target, templatePages = []) => {
  try {
    const orgId = await getCurrentOrgId();

    const closesUserManagement = !templatePages?.includes('app.users');
    if (target?.role === 'admin' && target?.is_active !== false && closesUserManagement) {
      const activeAdminCount = await countActiveAdmins(orgId);
      if (activeAdminCount <= 1) {
        return { data: null, error: null, blocked: true, reason: 'last_admin_users_page' };
      }
    }

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ permission_template_id: templateId })
      ?.eq('id', userId)
      ?.eq('organization_id', orgId)
      ?.select('id, permission_template_id')
      ?.single();

    if (error) throw error;
    return { data, error: null, blocked: false };
  } catch (error) {
    console.error('userManagementService.setPermissionTemplate error:', error);
    return { data: null, error, blocked: false };
  }
};

/**
 * Activate or deactivate a user account.
 * Performs last-admin check before deactivating an admin.
 *
 * @param {string} userId   - target user's id
 * @param {boolean} isActive - new value
 * @param {string} currentRole - current role of the target user
 */
export const setUserActive = async (userId, isActive, currentRole) => {
  try {
    const orgId = await getCurrentOrgId();

    // Last-admin guardrail: if we're deactivating an admin, ensure at least one remains
    if (!isActive && currentRole === 'admin') {
      const activeAdminCount = await countActiveAdmins(orgId);
      if (activeAdminCount <= 1) {
        return {
          data: null,
          error: null,
          blocked: true,
          reason: 'last_admin',
        };
      }
    }

    const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: isActive })?.eq('id', userId)?.eq('organization_id', orgId)?.select('id, is_active')?.single();

    if (error) throw error;
    return { data, error: null, blocked: false };
  } catch (error) {
    console.error('userManagementService.setUserActive error:', error);
    return { data: null, error, blocked: false };
  }
};

/**
 * Invite a new user to the current organization via the invite-user Edge Function.
 * The Edge Function determines the organization from the caller's session — never
 * pass organization_id in the payload.
 *
 * @param {object} payload - { email, full_name?, role?, language?, phone?,
 *                             product_access?, mode: 'invite'|'password', temp_password? }
 * @returns {{ data, error, status }} where status is the HTTP status code.
 */
/**
 * Set a user's trade (organization_trades.id), or clear it.
 *
 * Le metier est FACULTATIF : un charge de projet n'en a pas. null efface.
 * RLS fait respecter la portee organisation.
 *
 * @param {string} userId
 * @param {string|null} tradeId
 */
export const setUserTrade = async (userId, tradeId) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ trade_id: tradeId || null })
      ?.eq('id', userId)
      ?.select('id, trade_id')
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('userManagementService.setUserTrade error:', error);
    return { data: null, error };
  }
};

export const inviteUser = async (payload) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) {
      return { data: null, error: 'not authenticated', status: 401 };
    }

    const { data, error } = await supabase?.functions?.invoke('invite-user', {
      body: payload,
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });

    if (error) {
      // supabase.functions.invoke surfaces HTTP errors via the error object.
      // The actual HTTP body (with the server's error message) may be in
      // error.context (a Response object) or in data when status != 2xx.
      let status = error?.status ?? 500;
      let message = error?.message ?? 'Unknown error';

      // Try to extract the server's JSON error body for a richer message.
      try {
        const context = error?.context;
        if (context && typeof context?.json === 'function') {
          const body = await context?.json();
          if (body?.error) message = body?.error;
          if (context?.status) status = context?.status;
        }
      } catch (_) {
        // ignore parse failures — use the message we already have
      }

      return { data: null, error: message, status };
    }

    // Successful 2xx response
    return { data, error: null, status: 200 };
  } catch (err) {
    console.error('userManagementService.inviteUser error:', err);
    return { data: null, error: err?.message ?? 'Unknown error', status: 500 };
  }
};
